/**
 * Free-of-charge (FOC) supporting document — the one PDF / PNG / JPG the
 * cashier attaches to an FOC sale (POST /foc-document), whose id then rides on
 * the sale as `focDocumentId`.
 *
 * The checks here mirror the server's (415 FOC_DOCUMENT_TYPE_NOT_ALLOWED,
 * 413 FOC_DOCUMENT_TOO_LARGE) so a wrong file is refused before it is uploaded;
 * the server stays the authority.
 */

export const FOC_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

export const FOC_DOCUMENT_MIME_TYPES = ['application/pdf', 'image/png', 'image/jpeg'] as const;

/** `accept` attribute for the file picker. */
export const FOC_DOCUMENT_ACCEPT = FOC_DOCUMENT_MIME_TYPES.join(',');

/** Extension fallback for a browser that reports an empty `File.type`. */
const EXTENSION_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

/** The file's MIME type, falling back to its extension when the browser gave none. */
export function focDocumentMimeType(file: { name: string; type: string }): string {
  const type = (file.type ?? '').trim().toLowerCase();
  if (type) return type === 'image/jpg' ? 'image/jpeg' : type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_MIME[ext] ?? '';
}

/**
 * Cashier-facing reason a file cannot be used as the FOC document, or null
 * when it is acceptable (type is checked before size).
 */
export function validateFocDocument(file: { name: string; type: string; size: number }): string | null {
  const mime = focDocumentMimeType(file);
  if (!(FOC_DOCUMENT_MIME_TYPES as readonly string[]).includes(mime)) {
    return 'Dokumen harus berupa PDF, PNG, atau JPG.';
  }
  if (file.size <= 0) return 'File dokumen kosong. Pilih file lain.';
  if (file.size > FOC_DOCUMENT_MAX_BYTES) return 'Ukuran dokumen maksimal 10 MB.';
  return null;
}

export function isFocDocumentImage(mimeType: string): boolean {
  return mimeType === 'image/png' || mimeType === 'image/jpeg';
}

/** "2.4 MB" / "830 KB" — the size line under the file name. */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toLocaleString('id-ID', { maximumFractionDigits: 1 })} MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('id-ID')} KB`;
}

/**
 * FOC rejections from POST /foc-document, POST /sale and POST /sale/quote, by
 * the body's `errorCode`.
 */
const FOC_ERROR_COPY: Record<string, string> = {
  FOC_DOCUMENT_TOO_LARGE: 'Ukuran dokumen maksimal 10 MB.',
  FOC_DOCUMENT_TYPE_NOT_ALLOWED: 'Dokumen harus berupa PDF, PNG, atau JPG.',
  FOC_DOCUMENT_REQUIRED: 'Tiket FOC memerlukan dokumen pendukung. Unggah dokumen terlebih dahulu.',
  FOC_DOCUMENT_WITHOUT_FOC_ITEMS:
    'Dokumen FOC hanya untuk transaksi tiket FOC. Kosongkan keranjang dan mulai dari halaman Tiket FOC.',
  FOC_CART_INVALID:
    'Keranjang FOC hanya boleh berisi tiket masuk FOC (tanpa paket). Periksa kembali keranjang.',
  FOC_DOCUMENT_NOT_FOUND: 'Dokumen FOC tidak ditemukan. Unggah ulang dokumen.',
  FOC_DOCUMENT_ALREADY_USED:
    'Dokumen FOC ini sudah dipakai untuk transaksi lain. Unggah dokumen baru.',
};

/**
 * Indonesian message for an FOC rejection, or null when the error is not an
 * FOC one (callers fall back to their generic handling).
 */
export function focErrorMessage(err: unknown): string | null {
  const code = err && typeof err === 'object' ? (err as { errorCode?: unknown }).errorCode : null;
  if (typeof code !== 'string' || !code) return null;
  return Object.prototype.hasOwnProperty.call(FOC_ERROR_COPY, code)
    ? (FOC_ERROR_COPY[code] ?? null)
    : null;
}
